import { html, PropertyValues, unsafeCSS } from 'lit'
import { customElement, queryAssignedElements } from 'lit/decorators.js'
import { filter, fromEvent, map, merge, takeUntil } from 'rxjs'
import style from './radio-group.scss?inline'
import { HostAriaLabelMixin, SchmancyElement } from '@schmancy/mixins/index'
import { when } from 'lit/directives/when.js'
import { FormFieldMixin } from '@schmancy/mixins/formField.mixin'
import type { RadioButton } from './radio-button.ts'
import '@schmancy/primitives/typography/typography'

export type SchmancyRadioGroupChangeEvent = CustomEvent<{ value: string }>

/**
 * @deprecated Use `schmancy-chips` with `schmancy-filter-chip` children for small
 * option sets. Single selection is the default; add `required` to prevent deselection.
 */
@customElement('schmancy-radio-group')
export class RadioGroup extends HostAriaLabelMixin(FormFieldMixin(SchmancyElement)) {
	static styles = [unsafeCSS(style)]
	// `label`, `name`, `value`, `required`, `error`, `validationMessage` — all from the mixin.

	@queryAssignedElements({ flatten: true, selector: 'schmancy-radio-button' })
	private buttons!: RadioButton[]

	connectedCallback() {
		super.connectedCallback()

		// A click and an arrow key are the same act — "this option is now the
		// choice" — so both legs resolve to the option itself and one terminal
		// effect selects it.
		merge(
			fromEvent<CustomEvent<{ value: string }>>(this, 'radio-button-click').pipe(
				map(e => this.buttons.find(b => b.value === e.detail.value)),
			),
			// Each option renders its own <input type="radio"> inside its own shadow
			// root, where the platform's radio grouping — which is scoped to one tree
			// — never forms. Without this the group is N tab stops whose arrow keys do
			// nothing, instead of the one stop with arrow-key selection a radio group
			// is by definition.
			fromEvent<KeyboardEvent>(this, 'keydown').pipe(
				map(e => {
					const enabled = this.buttons.filter(b => !b.disabled)
					if (enabled.length === 0) return undefined
					// Focus outside the group reads as -1, so ArrowDown lands on the first
					// option and ArrowUp on the last.
					const current = enabled.indexOf(document.activeElement as RadioButton)
					switch (e.key) {
						case 'ArrowDown':
						case 'ArrowRight':
							e.preventDefault()
							return enabled[(current + 1) % enabled.length]
						case 'ArrowUp':
						case 'ArrowLeft': {
							e.preventDefault()
							const from = current === -1 ? enabled.length : current
							return enabled[(from - 1 + enabled.length) % enabled.length]
						}
						case 'Home':
							e.preventDefault()
							return enabled[0]
						case 'End':
							e.preventDefault()
							return enabled[enabled.length - 1]
						default:
							return undefined
					}
				}),
			),
		)
			.pipe(
				filter((btn): btn is RadioButton => btn !== undefined),
				takeUntil(this.disconnecting),
			)
			.subscribe(btn => {
				// An option chosen is entry finished — a radio group has no
				// mid-entry state to protect, so it is touched on the act.
				this.markTouched()
				this.value = btn.value
				this.emitChange({ value: btn.value })
				this.syncChildren()
				btn.focus()
			})
	}

	override firstUpdated(changed: PropertyValues) {
		super.firstUpdated(changed)
		this.syncChildren()
	}

	updated(changed: Map<string, unknown>) {
		super.updated(changed)
		if (changed.has('value')) this.syncChildren()
	}

	override resetForm() {
		super.resetForm()
		this.syncChildren()
	}

	private syncChildren() {
		const enabled = this.buttons.filter(b => !b.disabled)
		const active = enabled.find(b => b.value === this.value) ?? enabled[0]
		this.buttons.forEach(btn => {
			btn.checked = btn.value === this.value
			btn.tabbable = btn === active
		})
	}

	render() {
		return html`
			<div
				role="radiogroup"
				aria-label=${this.ariaLabel || this.label}
				aria-required=${this.required}
				aria-invalid=${this.error ? 'true' : 'false'}
				class=${this.classMap({
					'grid gap-2': true,
					'rounded-lg outline-solid outline-2 outline-error-default p-2': this.error,
				})}
			>
				${when(
					this.label,
					() => html`<schmancy-typography type="label" token="lg">${this.label}</schmancy-typography>`,
				)}
				<slot></slot>
			</div>
			${when(
				this.error && this.validationMessage,
				() => html`<div role="alert" class="text-error-default text-xs mt-1">${this.validationMessage}</div>`,
			)}
		`
	}
}

declare global {
	interface HTMLElementTagNameMap {
		'schmancy-radio-group': RadioGroup
	}
}
